/**
 * PANELİM KİMİN PANOSU — rol ve departmana göre DÜZEN (6 Ekim 2026, kullanıcı isteği: «her rol ve
 * departman için dashboard kısmını onun için en optimize, kolay ve iyi bir şekilde düzenle»).
 *
 * ERİŞİMİ DEĞİL, DÜZENİ BELİRLER. Her blok hâlâ kendi kapısını kendi taşıyor (yetkisi olmayana
 * `null` döner, sorgusu `enabled:` ile kapalı — `check:screen-access` bunu sayıyor). Buradaki tek
 * soru: kapısı açık olan bloklardan HANGİSİ ÜSTTE ve HANGİSİ TAM, hangisi ÖZET çizilecek.
 *
 * NEDEN GEREKTİ. Sıra herkes için aynıydı (onay → İK → depo → etkinlik → kanal → yönetim →
 * kişi). Pazarlama müdürü panelini açınca en üstte «Depo» bloğunu görüyordu — stok okuma
 * anahtarı olduğu için — ve kendi işi olan kongreler ikinci sıradaydı. Patron bütün
 * departmanların TAM bloklarını alt alta kaydırıyordu; İK, kongre kayıt anahtarı yüzünden
 * pazarlamanın bütçe tablosunu görüyordu.
 *
 * ROL İSTEMCİDE YOK, ANAHTAR VAR. Oturum rol kodu taşımıyor (`/auth/session`: izinler, kapsam,
 * modüller); kişinin «asıl işi» onu ayırt eden YÖNETME anahtarından okunuyor. Sıra önemli: patron
 * her alanı OKUYABİLİR ama hiçbirini yönetmez; İK müdürü kongre katılımcısı da yönetebilir ama
 * asıl işi İK'dır. Anahtar SELF kapsamla tutuluyorsa sayılmaz (`canForOthers`): kendi izin
 * talebini açabilen herkes İK değildir.
 *
 * DÖRT BÖLÜM, HER ROLDE AYNI SIRA (30 Ekim 2026, kullanıcının onayladığı düzen): «1. Duyurular ve
 * bildirimler en üstte. 2. Sonra takvim … 3. Sonra izin durumu ve araç. 4. Sonra departmana ait
 * bloklar.» İlk üç bölüm herkeste aynı yerde (`FIXED_ORDER`); rolün farkı DÖRDÜNCÜ bölümde: önce
 * onay bekleyenler ve atanan görevler, sonra rolün masası TAM, sonra okuyabildiği başka
 * departmanların ÖZETİ. Kaynak: rol bazlı ana sayfada yalnız o rolün giriş noktaları (SAP Fiori),
 * duyuru ve bekleyen iş üstte (Workday), yönetim panosunda az sayaç ve önce istisna, ilaç sahasında
 * ana sayfa günün programı ve görevler (Veeva).
 *
 * TEK İSTİSNA PATRON VE YÖNETİCİ (31 Ekim 2026): orada iş takvimin hemen ardından geliyor, «İzin
 * durumum» ve «Aracım» sayfanın sonunda (`WORK_FIRST`).
 */

export type DashboardPersona =
  | "admin"
  | "executive"
  | "hr"
  | "marketing"
  | "warehouse"
  | "sales"
  | "fleet"
  | "regional"
  | "manager"
  /** Saha — hekim görüşmesi kaydeden mümessil (Panelim 30 Ekim 2026). */
  | "field"
  | "employee";

export type DashboardBlockId =
  | "approvals"
  | "executive"
  /** «Ekibim» — bölge müdürünün ekibi (rol-yetki 21 Ekim 2026). */
  | "team"
  /** «Ekipten geri bildirimler» — üst yönetim zincirindeki okuyucuya (rol-yetki 21 Ekim 2026). */
  | "feedback"
  | "hr"
  | "events"
  | "inventory"
  | "sales"
  | "channel"
  | "fleet"
  /** «Mali işler» — ödeme bekleyen ve bu ay gider (finans F2c-1, 2 Kasım 2026): muhasebe ve finansın panosu. */
  | "finance"
  | "admin"
  /** «Saha» — hekim görüşmeleri ve sunum talepleri: mümessilin masası, bölge müdürünün ikinci bloğu. */
  | "field"
  | "mywork"
  /** «Takvimim» — kişinin önümüzdeki günleri (28 Ekim 2026). Herkeste, kapısız. */
  | "calendar"
  | "feed";

/** Tam: departmanın masası. Özet: sayaçlar ve bir bağlantı — başkasının işine göz atmak. */
export type BlockDensity = "full" | "compact";

export interface BlockLayout {
  /** Görsel sıra (küçük olan üstte). Bloklar kaynakta sabit; yalnız gösterim sırası değişiyor. */
  order: number;
  density: BlockDensity;
  /**
   * Bu rolde blok hiç çizilmiyor (sorguları da kapalı). Yalnız patronda: İK, stok ve kanal
   * sayıları «Şirket özeti»nde zaten var; altta bir kez daha özetlemek aynı sayıyı iki kez
   * okutuyordu. Verilmezse görünür.
   */
  visible?: boolean;
}

interface KeyCheck {
  canForOthers(key: string): boolean;
  /** Filo bir iş modülü: anahtar ancak modül açıkken bir masa demek. Verilmezse açık sayılır. */
  isModuleEnabled?(key: string): boolean;
}

/** Bir departmanın masasını YÖNETEN anahtarlar: bunlardan birini taşıyan kişi o masanın kişisidir, gözlemcisi değil. */
const DESK_MANAGE_KEYS = [
  "hr.employee.manage",
  "events.event.manage",
  "inventory.document.post",
  "inventory.document.create",
  "channel.report.import",
  "sales.order.manage",
  "fleet.vehicle.manage",
] as const;

/**
 * PATRON: şirketi OKUR ama hiçbir masayı yönetmez (rol-yetki 21 Ekim 2026). Tespit edici anahtar `audit.log.read` idi;
 * «patronun yönetim denetimini görmesine gerek yok» kararıyla patrondan çıkınca ayıran şey İKİ OKUMA oldu — personeli
 * (`hr.employee.read`) ve kongreyi (`events.event.read`) okuyan, ama İK/pazarlama/depo/satış/filo masalarından hiçbirini
 * yönetmeyen. Tek bir okuma (yalnız İK'yı okuyan bir uzman, yalnız kongreyi okuyan bir yönetici) patron DEĞİL: onlar
 * panelde kendi departmanının özetini görmeli (`HrPanelBlock` özet hâli) ve «Şirket özeti» onlar için anlamsız olurdu.
 *
 * ÜÇÜNCÜ ANAHTAR, 26 Ekim 2026: `hr.employee.read_sensitive`. O gün yöneticiye işletme görünürlüğü verildi
 * (`hr.employee.read` + filo + ürün talebi) ve iki okumalı tanım YÖNETİCİYİ DE patron saymaya başladı —
 * panoda ölçüldü: başlığın altında patronun cümlesi yazıyordu. İkisini ayıran şey okumanın GENİŞLİĞİ değil
 * DERİNLİĞİ: özlük dosyasının hassas alanları (TC, IBAN) patronda adıyla var, genel müdür yardımcısında
 * bilerek yok. Yönetici artık `manager` kalıyor ve «Şirket özeti»ni `showsCompanySummary` ile alıyor —
 * aynı ızgara, kendi anahtarlarının doldurduğu kadarıyla.
 */
function observesCompany(has: (key: string) => boolean): boolean {
  return (
    has("hr.employee.read") &&
    has("hr.employee.read_sensitive") &&
    has("events.event.read") &&
    !DESK_MANAGE_KEYS.some((key) => has(key))
  );
}

/**
 * Kişinin asıl işi. İlk eşleşen kazanır; sıra «en geniş sorumluluk önce».
 *
 * - admin: kullanıcı VE rol okuyan — kurulumu yöneten kişi.
 * - executive: şirketi okuyan ama hiçbir masayı yönetmeyen — patron. (21 Ekim 2026'a kadar ayıran anahtar
 *   `audit.log.read`ti; «patronun yönetim denetimini görmesine gerek yok» kararıyla patrondan çıkınca ayıran şey
 *   «personeli ve kongreyi okur, masa yönetmez» oldu — `observesCompany`. `audit.log.read` taşıyan başka bir rol hâlâ
 *   patron panosunu alır.)
 * - hr / marketing / warehouse / sales: departmanının kaydını YÖNETEN.
 * - fleet: filo araçlarını YÖNETEN (filo ana uygulamaya alındı, 10 Ekim 2026) — onay da veriyor
 *   olsa asıl masası filo; «manager»dan önce.
 * - regional: «Ekibim»i açabilen — bölge müdürü (rol-yetki 21 Ekim 2026). Onay kutusu hâlâ en üstte; asıl
 *   bloğu ekibi.
 * - manager: departman yönetmeyen ama onay veren — yönetici.
 * - field: hekim görüşmesi KAYDEDEN (`events.physician_visit.record`) — mümessil (30 Ekim 2026). Bölge
 *   müdürü de kaydedebiliyor ama ekibi var, `regional` önce geliyor.
 * - employee: geri kalan herkes.
 */
export function dashboardPersona(permissions: KeyCheck): DashboardPersona {
  const has = (key: string) => permissions.canForOthers(key);
  if (has("identity.user.read") && has("authorization.role.read")) return "admin";
  if (has("audit.log.read") || observesCompany(has)) return "executive";
  if (has("hr.employee.manage")) return "hr";
  if (has("events.event.manage")) return "marketing";
  if (has("inventory.document.post") || has("inventory.document.create")) return "warehouse";
  if (has("channel.report.import") || has("sales.order.manage")) return "sales";
  if (has("fleet.vehicle.manage") && (permissions.isModuleEnabled?.("module.fleet") ?? true)) {
    return "fleet";
  }
  if (has("hr.team.read")) return "regional";
  if (has("hr.leave.approve") || has("events.event.approve")) return "manager";
  if (has("events.physician_visit.record")) return "field";
  return "employee";
}

/**
 * «Ekipten geri bildirimler» bloğunun SORGUSU hangi panolarda açılır — ÜST YÖNETİMİN panolarında (30 Ekim 2026).
 *
 * Kullanıcı: «burası direkt yönetim ve patrona gidecek; İK buradaki yazıları göremeyecek». 21 Ekim'den beri okuma örgüt
 * şemasından türüyordu (yazarın biriminin üstündeki birimlerin başları) ve blok çalışan, sistem yöneticisi ve PATRON
 * DIŞINDAKİ her panoda açıktı — yani patron göremiyordu, satış müdürü görüyordu. Zincir kalktı: okuyucu yönetici ve patron
 * (`hr.team_feedback.read_all`, bloğun kendi kapısı). Sistem yöneticisi anahtarı katalogdan taşıyor ama bu bir okuyucu
 * panosu değil. `showsCompanySummary` ile aynı iki pano.
 */
export function readsFeedbackFromBelow(persona: DashboardPersona): boolean {
  return persona === "executive" || persona === "manager";
}

/** Kişinin kendi departman bloğu — panoda onaydan hemen sonra, TAM çizilen. */
const PRIMARY_BLOCK: Record<DashboardPersona, DashboardBlockId> = {
  admin: "admin",
  executive: "executive",
  hr: "hr",
  marketing: "events",
  warehouse: "inventory",
  // Teklif ve sipariş geri döndü (17 Ekim 2026): satışın masası onlar; kanal hemen arkasında.
  sales: "sales",
  fleet: "fleet",
  regional: "team",
  /**
   * YÖNETİCİNİN ASIL BLOĞU «ŞİRKET ÖZETİ» OLDU — 26 Ekim 2026, kullanıcı: «yöneticinin
   * panelinde daha çok bilgi verilmesi gerekiyor işletme hakkında … müdür, yönetici falan çok
   * az şey görüyor ve çok az şeye hakim».
   *
   * Önceki asıl blok `approvals` idi ve o zaten her panoda birinci sırada (kural 1): yani
   * yöneticinin panosunda İKİNCİ sıra boştu ve altında yalnız özet departman blokları vardı.
   * Genel müdür yardımcısı için doğru ikinci sıra, şirketin bugünkü sayıları.
   */
  manager: "executive",
  /** Mümessilin masası sahası: bu ayki görüşmeleri, düzeltme istenenler, sunum talepleri. */
  field: "field",
  employee: "mywork",
};

/**
 * ROLÜN İKİNCİ TAM BLOĞU — asıl bloğun hemen ardından ve özetlenmeden (30 Ekim 2026 düzeni).
 *
 * Satışın iki masası var (teklif/sipariş ve kanal); bölge müdürünün ekibinden sonra sahası
 * (kararını bekleyen görüşmeler, gelen sunum talepleri); yönetici ve patronun şirket özetinden sonra
 * ekipten gelen geri bildirimler.
 */
const SECONDARY_BLOCK: Partial<Record<DashboardPersona, DashboardBlockId>> = {
  sales: "channel",
  regional: "field",
  manager: "feedback",
  executive: "feedback",
};

/**
 * BU ROLDE HİÇ ÇİZİLMEYEN bloklar (sorguları da kapalı).
 *
 * - patron: İK, stok ve kanal sayıları «Şirket özeti»nde zaten var.
 * - yönetici: patronla aynı gerekçe (30 Ekim 2026). İK özetinin dört sayısının üçü (aktif çalışan,
 *   bugün izinli, onay bekleyen izin), stoğun miadı ve kanalın dosya sayısı şirket özetinde de
 *   yazıyordu — aynı sayıyı iki kez okutmak.
 * - patron ve yönetici, 31 Ekim 2026: SATIŞ ve FİLO da. «Şirket özeti» artık onay ve sevk bekleyen
 *   siparişi, filo vadesini ve yolda olmayan aracı sayıyor; altta ikinci kez özetlemek yine aynı sayı.
 * - sistem yöneticisi: her anahtarı katalogdan taşıyor ama departmanların masası onun işi değil; panosu
 *   kurulumun durumu (SAP Fiori: rolün giriş noktaları, çok rollü hesapta dağınıklık yok).
 */
const HIDDEN_FOR: Partial<Record<DashboardPersona, ReadonlySet<DashboardBlockId>>> = {
  // «Mali işler» (2 Kasım 2026): patron ve yöneticide iki sayısı «Şirket özeti»nin «Mali işler» grubunda zaten var.
  executive: new Set(["hr", "inventory", "channel", "sales", "fleet", "finance"]),
  manager: new Set(["hr", "inventory", "channel", "sales", "fleet", "finance"]),
  admin: new Set(["team", "feedback", "field", "hr", "events", "inventory", "sales", "channel", "fleet", "finance", "executive"]),
};

/**
 * «ŞİRKET ÖZETİ» HANGİ PANODA ÇİZİLİR — 26 Ekim 2026'ya kadar yalnız patronda.
 *
 * Blok zaten HER SAYACINI kendi anahtarıyla çiziyor: okunamayan alanın sayacı ızgarada yok ve
 * sorgusu atılmıyor. Yani ikinci bir panoya açmak kimseye yeni veri vermiyor — yalnız kişinin
 * ZATEN okuyabildiğini tek ızgarada topluyor. Yönetici (genel müdür yardımcısı) tam olarak bu
 * ızgaranın okuyucusu; kararı o veriyor ve kararı verirken şirketin sayılarına bakması gerekiyor.
 */
export function showsCompanySummary(persona: DashboardPersona): boolean {
  return persona === "executive" || persona === "manager";
}

/** «Saha» bloğunun çizildiği panolar: mümessil ve bölge müdürü. */
const FIELD_PERSONAS: ReadonlySet<DashboardPersona> = new Set(["field", "regional"]);

/**
 * İLK ÜÇ BÖLÜM HERKESTE AYNI YERDE (30 Ekim 2026): akış, takvim, kişinin izin durumu ve aracı.
 * Onay bekleyenler ve atanan görevler dördüncü bölümün başında — bekleyen iş varsa zil ve
 * «Bildirimlerim» zaten en üstte haber veriyor; blok kararın verileceği yer. Patron ve yöneticide
 * izin durumu ve araç en sonda (`WORK_FIRST`).
 */
const FIXED_ORDER: Partial<Record<DashboardBlockId, number>> = {
  feed: 0,
  calendar: 1,
  mywork: 2,
  approvals: 3,
};

/** Dördüncü bölümün başladığı sıra: rolün asıl bloğu. */
const ROLE_SECTION = 4;

/** Asıl ve ikinci blok dışındakilerin sırası — başka departmanların özeti, bu sırayla. */
const DEFAULT_SEQUENCE: readonly DashboardBlockId[] = [
  "team",
  "feedback",
  "field",
  "hr",
  "events",
  "inventory",
  "sales",
  "channel",
  "fleet",
  "finance",
  "admin",
];

/**
 * PATRON VE YÖNETİCİDE İŞ TAKVİMİN HEMEN ARDINDAN (31 Ekim 2026, kullanıcı: «patron ve yönetici
 * kısmında bu kısımlar ve işle ilgili olan kısımlar takvimden sonra gelsin»).
 *
 * Dört bölüm kuralının tek istisnası. Bu iki panoyu açan kişi şirketin işine bakmak için açıyor:
 * onayını bekleyenler, şirket özeti, geri bildirimler ve kongreler takvimin altında; kendi izin
 * durumu ve aracı en sonda. Öteki rollerde üçüncü bölüm yerinde kalıyor.
 */
const WORK_FIRST: ReadonlySet<DashboardPersona> = new Set(["executive", "manager"]);

/** `WORK_FIRST` panolarında «İzin durumum» ile «Aracım»ın yeri: her iş bloğunun ardı. */
const PERSONAL_LAST = ROLE_SECTION + 2 + DEFAULT_SEQUENCE.length + 1;

/**
 * Bir bloğun bu kişideki yeri ve yoğunluğu.
 *
 * KURALLAR (30 Ekim 2026, kullanıcının onayladığı düzen; patron ve yönetici istisnası `WORK_FIRST`):
 * 1. Duyurular ve bildirimler en üstte; sonra takvim (ajandayla); sonra izin durumu ve araç —
 *    her rolde aynı yer (`FIXED_ORDER`).
 * 2. Dördüncü bölüm onay bekleyenlerle ve atanan görevlerle başlıyor (yoksa çizilmiyor).
 * 3. Sonra rolün asıl bloğu TAM; varsa ikinci bloğu da TAM (`SECONDARY_BLOCK`).
 * 4. Okuyabildiği başka departmanların blokları ÖZET: sayaçlar ve «Tümünü gör».
 * 5. Rolün işi olmayan ya da sayıları zaten özette olan bloklar hiç çizilmiyor (`HIDDEN_FOR`).
 *
 * ÖNCEKİ DÜZEN (6–7 Ekim 2026) karar → asıl iş → kişi → akış → başka departmanlar idi; duyurular
 * sayfanın ortasına, takvim kişinin işlerinin altına düşüyordu.
 */
export function blockLayout(persona: DashboardPersona, block: DashboardBlockId): BlockLayout {
  if (block === "mywork" && WORK_FIRST.has(persona)) return { order: PERSONAL_LAST, density: "full" };
  const fixed = FIXED_ORDER[block];
  if (fixed !== undefined) return { order: fixed, density: "full" };

  const index = DEFAULT_SEQUENCE.indexOf(block);
  const order = ROLE_SECTION + 2 + (index === -1 ? DEFAULT_SEQUENCE.length : index);
  if (HIDDEN_FOR[persona]?.has(block)) return { order, density: "compact", visible: false };
  /**
   * «SAHA» YALNIZ SAHANIN PANOSUNDA (31 Ekim 2026, tarayıcıda ölçüldü): pazarlama müdürü sunum
   * isteyebildiği için blok onun panosuna da düşüyordu — onaylanan düzende yok. Blok mümessilin
   * masası ve bölge müdürünün ekibinin sahası; başka bir rolün özeti değil.
   */
  if (block === "field" && !FIELD_PERSONAS.has(persona)) {
    return { order, density: "compact", visible: false };
  }

  if (block === PRIMARY_BLOCK[persona]) return { order: ROLE_SECTION, density: "full" };
  if (block === SECONDARY_BLOCK[persona]) return { order: ROLE_SECTION + 1, density: "full" };
  /* «Şirket özeti» kendi kapısıyla (`showsCompanySummary`) yalnız patron ve yöneticide çiziliyor. */
  if (block === "executive") return { order: ROLE_SECTION, density: "full" };

  // Ekip ve geri bildirim kişinin kendi ekibi ve kendisine gelen notlar — başkasının masası değil, özetlenmiyor.
  if (block === "team" || block === "feedback") return { order, density: "full" };
  return { order, density: "compact" };
}

/**
 * Tailwind sıra sınıfları SABİT yazılıyor: JIT, `order-${n}` gibi kurulan bir adı göremez ve
 * sınıfı üretmez. Kaynakta tam adıyla geçen sınıf üretilir.
 */
const ORDER_CLASSES = [
  "order-first",
  "order-1",
  "order-2",
  "order-3",
  "order-4",
  "order-5",
  "order-6",
  "order-7",
  "order-8",
  "order-9",
  "order-10",
  "order-11",
] as const;

export function orderClass(order: number): string {
  return ORDER_CLASSES[Math.min(Math.max(order, 0), ORDER_CLASSES.length - 1)] ?? "order-last";
}

/**
 * Sayfa başlığının altındaki cümle — kişinin panoyu NE İÇİN açtığı. 30 Ekim 2026'dan beri her panoda
 * duyurular, takvim ve izin durumu önde; cümle dördüncü bölümü, yani rolün masasını adlandırıyor.
 */
export const PERSONA_DESCRIPTION: Record<DashboardPersona, string> = {
  admin: "Duyurular, ajandanız ve kurulumun durumu: kullanıcılar, roller ve modüller.",
  executive: "Duyurular, takviminiz ve şirketin bugünkü özeti.",
  hr: "Duyurular, takviminiz, izin durumunuz ve İK masanız.",
  marketing: "Duyurular, takviminiz, izin durumunuz ve pazarlama masanız: kongreler, sunum ve ürün talepleri.",
  warehouse: "Duyurular, takviminiz, izin durumunuz ve depo masanız: miad, kritik stok, sevk ve sayım.",
  sales: "Duyurular, takviminiz, izin durumunuz ve satış masanız: teklifler, siparişler ve kanal.",
  fleet: "Duyurular, takviminiz, izin durumunuz ve filo masanız: vadeler, cezalar ve araçlar.",
  regional: "Duyurular, takviminiz, izin durumunuz; onayınızı bekleyenler, ekibiniz ve sahadaki işler.",
  manager: "Duyurular ve takviminiz; onayınızı bekleyenler ve şirketin bugünkü durumu; en altta izin durumunuz.",
  field: "Duyurular, bugünkü planınız, izin durumunuz ve sahadaki işleriniz.",
  employee: "Duyurular, takviminiz, izin durumunuz ve kendi kayıtlarınız.",
};

/**
 * Üst çubukta adın altındaki etiket (6 Ekim 2026). Eskiden kişinin menüsünde satırı olan SON
 * alandan okunuyordu: patron ve İK müdürü «Yönetim», yönetici «Pazarlama» yazıyordu — üçü de
 * kişinin işi değildi. Çalışan için verilmiyor: o durumda eski hesap (menü alanı) sürüyor, çünkü
 * çalışan etiketi satıcının kendi hesabında da yanlış olurdu.
 */
export const PERSONA_LABEL: Partial<Record<DashboardPersona, string>> = {
  admin: "Sistem yönetimi",
  executive: "Patron",
  hr: "İK",
  marketing: "Pazarlama",
  warehouse: "Depo",
  sales: "Satış",
  fleet: "Filo",
  regional: "Bölge müdürü",
  manager: "Yönetici",
};
